/**
 * Stripe.js loader for the web client (ELARA-1012).
 *
 * `loadStripe` injects a script tag and is expensive, so it is called once per publishable key and
 * the promise reused. Deliberately lazy — `getStripe()` is only reached from the membership payment
 * surface, so a member on a comped invite (or any page that never collects a card) never downloads
 * Stripe.js at all.
 *
 * The publishable key is not a secret; it is safe in the bundle by design. It is a BUILD-time value
 * though — Vite inlines it — so it lives in the `build_frontend` job's `env:` block rather than the
 * Cloud Run runtime env. When it is absent, `getStripe()` returns null and the caller surfaces a
 * configuration error rather than rendering dead card fields.
 */

import type { Stripe } from '@stripe/stripe-js';

import { loadStripe } from '@stripe/stripe-js';

import { Env } from '#env.js';

let stripePromise: Promise<Stripe | null> | undefined;

/** True when a publishable key was built in, so the caller can branch before rendering. */
export function isStripeConfigured(): boolean {
  return Boolean(Env.VITE_STRIPE_PUBLISHABLE_KEY?.trim());
}

/**
 * The shared Stripe.js instance, or null when no publishable key was configured at build time.
 *
 * Resolving to null rather than throwing keeps the failure handleable at the call site: signup can
 * show "payment is unavailable" instead of an unhandled rejection during render.
 */
export async function getStripe(): Promise<Stripe | null> {
  const key = Env.VITE_STRIPE_PUBLISHABLE_KEY?.trim();
  if (!key) return null;
  // Memoized on the promise, not the resolved value, so concurrent callers during the initial load
  // share one script injection rather than racing to start several.
  stripePromise ??= loadStripe(key);
  return stripePromise;
}
